import { logger } from "@/utils/logger";
import React, { useMemo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ExternalLink, Download, Youtube, Settings } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import ShowroomCards from './ShowroomCards';
import { openURL } from '@/utils/navigationUtils';
import { useLanguage } from '@/contexts/LanguageContext';
import { decodeText } from '@/utils/textDecoding';
import { getVehicleCardByStyleIdSync, isShowroomIndexReady } from '@/utils/showroomUtils';

interface ShowroomButton {
  Button_Text: string;
  Link: string;
}

interface SuggestedModel {
  styleid?: string | number; // Can receive just styleid for lookup
  styleId?: string | number; // Also accept camelCase version
  showroom_styleId?: string | number; // Also accept underscore version from AI
  Main_Picture_URL?: string;
  Year?: string;
  Make?: string;
  Model?: string;
  Trim?: string;
  Price?: number;
  Fuel_Type?: string;
  VDP_URL?: string;
  Vehicle_Description_Summary?: string;
  Features?: string[];
}

interface OEMContent {
  image_url: string[];
  summary: string;
  "360_images": string[];
}

interface ShowroomData {
  VDP?: ShowroomButton;
  Build_And_Price?: ShowroomButton;
  Brochure?: ShowroomButton;
  Suggested_Models?: SuggestedModel[];
  OEM_Content?: OEMContent;
}

interface ShowroomMessageProps {
  showroom: ShowroomData;
}

const ShowroomMessage: React.FC<ShowroomMessageProps> = ({ showroom }) => {
  const { language } = useLanguage();
  
  // Retry intervals: 1s, 3s, 5s (total 9 seconds before giving up)
  // DO NOT fall back to partial data - either we have full data or we don't render
  const [indexReady, setIndexReady] = useState<boolean | null>(null);
  
  useEffect(() => {
    // Check if we have styleId-based models that need the index
    const hasStyleIdModels = showroom.Suggested_Models?.some(m => 
      (m as any).styleid || (m as any).styleId || (m as any).showroom_styleId
    );
    
    if (!hasStyleIdModels) {
      // No styleId models, no need to wait for index
      setIndexReady(true);
      return;
    }
    
    // Check immediately
    if (isShowroomIndexReady()) {
      logger.log('✅ Showroom index ready immediately');
      setIndexReady(true);
      return;
    }
    
    // Trigger showroom loading if not already started
    import('@/utils/parallelDataLoader')
      .then(({ ensureShowroomReady }) => ensureShowroomReady())
      .catch(() => {
        logger.warn('⚠️ Failed to trigger showroom data loading');
      });
    
    // Retry with specific intervals: 1s, 3s, 5s (cumulative: 1s, 4s, 9s)
    const retryIntervals = [1000, 3000, 5000]; // 1s, 3s, 5s
    let retryIndex = 0;
    let cancelled = false;
    
    const attemptRetry = () => {
      if (cancelled) return;
      
      if (isShowroomIndexReady()) {
        logger.log(`✅ Showroom index ready after retry ${retryIndex}`);
        setIndexReady(true);
        return;
      }
      
      if (retryIndex < retryIntervals.length) {
        const delay = retryIntervals[retryIndex];
        logger.log(`🔄 Showroom index not ready, retry ${retryIndex + 1}/3 in ${delay}ms`);
        retryIndex++;
        setTimeout(attemptRetry, delay);
      } else {
        // All retries exhausted after 9 seconds total - DO NOT render partial data
        console.error('❌ Showroom index not ready after 9 seconds (3 retries) - cards will not render');
        setIndexReady(false);
      }
    };
    
    // Start first retry after 1 second
    setTimeout(attemptRetry, retryIntervals[0]);
    retryIndex = 1;
    
    return () => {
      cancelled = true;
    };
  }, [showroom.Suggested_Models]);
  
  
  // Helper function to validate button data
  const isValidButton = (button?: ShowroomButton) => {
    return button && 
           button.Button_Text && 
           button.Button_Text.trim() !== '' && 
           button.Link && 
           button.Link.trim() !== '';
  };

  // Helper function to validate YouTube video (Link is required, Button_Text is optional)
  const isValidYouTubeVideo = (video?: any) => {
    return video && video.Link && video.Link.trim() !== '';
  };

  // Get current page URL for comparison
  const getCurrentPageUrl = () => {
    try {
      const stored = localStorage.getItem('context-current-page');
      const context = stored ? JSON.parse(stored) : null;
      return context?.url || '';
    } catch (error) {
      return '';
    }
  };

  const currentPageUrl = getCurrentPageUrl();
  const buttons = [];

  // Hydrate models using styleid - preserve only lowestPayments, lowestPaymentsTerms, youtube_id
  // CRITICAL: Do NOT proceed if indexReady is false (timed out) - we need proper data
  const hydratedModels = useMemo(() => {
    if (!showroom.Suggested_Models) return [];
    
    // If index is not ready yet (null = still waiting, false = timed out), return empty
    if (indexReady !== true) {
      logger.log('🏢 SHOWROOM HYDRATION SKIPPED: indexReady =', indexReady);
      return [];
    }
    
    return showroom.Suggested_Models.map((model, index) => {
      // Check for styleid, styleId, and showroom_styleId (handle all variations)
      const styleIdValue = model.styleid || model.styleId || model.showroom_styleId;
      
      // If model has styleid and it's not empty, look it up and ignore all other fields except the preserved ones
      if (styleIdValue && String(styleIdValue).trim() !== '') {
        logger.log(`🏢 Looking up styleId: ${styleIdValue}`);
        const vehicleCard = getVehicleCardByStyleIdSync(styleIdValue);
        
        if (vehicleCard) {
          logger.log(`✅ Found vehicle card for styleId ${styleIdValue}:`, {
            make: vehicleCard.make,
            model: vehicleCard.model,
            hasImage: !!vehicleCard.mainPicture
          });
          
          // Map showroom fields to SuggestedModel format
          const hydratedModel = {
            // Preserve all style ID variants for downstream components
            showroom_styleId: styleIdValue,
            styleid: styleIdValue,
            styleId: styleIdValue,
            Main_Picture_URL: vehicleCard.mainPicture || '',
            Year: String(vehicleCard.year || ''),
            Make: vehicleCard.make || '',
            Model: vehicleCard.model || '',
            Trim: vehicleCard.trim || '',
            Fuel_Type: vehicleCard.drivetrain || '',
            Price: vehicleCard.finalPrice || 0,
            VDP_URL: (language === 'fr' ? vehicleCard.showroomUrlFrench : vehicleCard.showroomUrlEnglish) || '',
            Vehicle_Description_Summary: '',
            Features: [],
            Build_And_Price: (language === 'fr' ? vehicleCard.buildAndPriceUrlFrench : vehicleCard.buildAndPriceUrlEnglish) ? {
              Button_Text: language === 'fr' 
                ? `Configurer ${vehicleCard.make || ''} ${vehicleCard.model || ''}`.trim()
                : `Build & Price ${vehicleCard.make || ''} ${vehicleCard.model || ''}`.trim(),
              Link: (language === 'fr' ? vehicleCard.buildAndPriceUrlFrench : vehicleCard.buildAndPriceUrlEnglish) || ''
            } : undefined,
            // Preserve brochure metadata from AI response so components can fall back when showroom data lacks it
            Brochure: (model as any).Brochure,
            // Preserve these fields from the original model object (if provided)
            lowestPayments: (model as any).lowestPayments,
            lowestPaymentsTerms: (model as any).lowestPaymentsTerms,
            youtube_id: (model as any).youtube_id
          };
          
          return hydratedModel;
        } else {
          logger.warn(`❌ No vehicle card found for styleId ${styleIdValue} - card will be filtered out (no image)`);
          // Return model as-is - it will be filtered out below because it lacks Main_Picture_URL
        }
        // If showroom lookup failed, fall back to AI-provided fields
        // This will likely be filtered out if it lacks Main_Picture_URL
        return model as SuggestedModel;
      }
      // Model doesn't have styleid, return as-is
      return model;
    }).filter((model): model is SuggestedModel => model !== null);
  }, [showroom.Suggested_Models, language, indexReady]);

  // Filter out models where image is missing - cards without images look broken
  // An image is REQUIRED to display a showroom card
  const filteredModels = hydratedModels.filter(model => {
    const hasImage = model.Main_Picture_URL && model.Main_Picture_URL.trim() !== '';
    
    if (!hasImage) {
      logger.warn('⚠️ Filtered out showroom model with no image:', {
        styleId: model.styleid || model.styleId || model.showroom_styleId,
        make: model.Make,
        model: model.Model
      });
    }
    
    // Show card only if it has an image
    return hasImage;
  });
  
  logger.log('🏢 SHOWROOM FILTERING COMPLETE:', {
    hydratedCount: hydratedModels.length,
    filteredCount: filteredModels.length,
    filteredOut: hydratedModels.length - filteredModels.length
  });

  const hasSuggestedModels = filteredModels && Array.isArray(filteredModels) && filteredModels.length > 0;

  // Check VDP button - only add if valid and not current page
  if (isValidButton(showroom.VDP) && showroom.VDP!.Link !== currentPageUrl) {
    buttons.push({
      text: decodeText(showroom.VDP!.Button_Text.trim()),
      link: showroom.VDP!.Link,
      icon: ExternalLink,
      type: 'vdp'
    });
  }

  // Check Build & Price button - only add if valid and not current page
  if (isValidButton(showroom.Build_And_Price) && showroom.Build_And_Price!.Link !== currentPageUrl) {
    buttons.push({
      text: decodeText(showroom.Build_And_Price!.Button_Text.trim()),
      link: showroom.Build_And_Price!.Link,
      icon: 'fa-gear', // Special marker for FontAwesome icon
      type: 'build_and_price'
    });
  }

  // Check Brochure button - only add if valid and not current page
  if (isValidButton(showroom.Brochure) && showroom.Brochure!.Link !== currentPageUrl) {
    const buttonText = decodeText(showroom.Brochure!.Button_Text.trim());
    const isBuildButton = buttonText.toLowerCase().includes('build') || buttonText.toLowerCase().includes('construi');
    
    buttons.push({
      text: buttonText,
      link: showroom.Brochure!.Link,
      icon: isBuildButton ? Settings : Download,
      type: 'brochure'
    });
  }

  // ABSOLUTELY DO NOT RENDER if no valid content exists
  if (buttons.length === 0 && !hasSuggestedModels) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 my-3">
      {/* Showroom banner */}
      <div className="flex items-center justify-center h-10 bg-white rounded-md">
        <span 
          className="text-sm font-medium text-gray-700"
          dangerouslySetInnerHTML={{
            __html: hasSuggestedModels && filteredModels!.length === 1
              ? (language === 'fr' ? "Mod&egrave;le en salle d'exposition" : 'Showroom model')
              : (language === 'fr' ? "Salle d'exposition" : 'Showroom models')
          }}
        />
      </div>
      
      {/* Render other buttons */}
      {buttons.map((button, index) => {
        const IconComponent = button.icon;
        const isFontAwesome = typeof button.icon === 'string' && button.icon.startsWith('fa-');
        
        return (
          <Button
            key={index}
            variant="outline"
            className="w-full h-auto py-3 px-4 bg-white hover:bg-gray-50 text-gray-900 d2cbot-bot_border-gray-200"
            onClick={() => openURL(button.link)}
          >
            <div className="flex items-center gap-3 w-full min-w-0">
              {isFontAwesome ? (
                <FontAwesomeIcon icon={faGear} className="w-5 h-5 text-gray-500 flex-shrink-0" />
              ) : (
                <IconComponent className="w-5 h-5 text-gray-500 flex-shrink-0" />
              )}
              <span className="font-medium break-words text-left flex-1 min-w-0">{button.text}</span>
            </div>
          </Button>
        );
      })}
      
      {/* Render suggested models if available */}
      {hasSuggestedModels && (
        <div className="mt-2">
          <ShowroomCards models={filteredModels!} />
        </div>
      )}
    </div>
  );
};

export default ShowroomMessage;